/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import { Badge } from "@mui/material";
// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { useTranslation } from "react-i18next";
import { normalizeRoleLevel } from "utils/roleUtils";

// Material Dashboard 2 React context for roles
import { useUserContext } from "context/UserContext"; // 👈 1. Import your user context hook
import { APP_VERSION } from "version";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const currentPath = location.pathname;
  const { roleLevel } = useUserContext();
  const { t } = useTranslation("routes");
  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const getDisplayName = ({ key, name }) => {
    if (!key) return name;
    let translationKey = key;
    if (key === "warehouses") {
      translationKey = "warehouses.default";
    }
    const translated = t(translationKey);
    let displayName = translated && translated !== translationKey ? translated : name;
    if (typeof displayName === "object" && displayName !== null) {
      displayName = displayName.zh || displayName.en || "";
    }
    if (!displayName && key) displayName = key;
    return displayName;
  };

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes
    .filter((route) => !route.hidden) // First, filter out items explicitly marked as hidden
    .filter((route) => {
      if (route.type === "title" || route.type === "divider") {
        return true;
      }

      if (route.type === "collapse") {
        if (!Array.isArray(route.roleLevels) || route.roleLevels.length === 0) {
          return false;
        }
      }

      const numericRoleLevel = normalizeRoleLevel(roleLevel);
      if (numericRoleLevel && route.roleLevels.includes(numericRoleLevel)) {
        return true;
      }

      return false;
    })
    .map(({ type, name, icon, title, noCollapse, key, href, route }) => {
      let returnValue;

      const displayName = getDisplayName({ key, name });

      if (type === "collapse") {
        const isNotificationItem = key === "notifications";
        const wrappedIcon = isNotificationItem ? (
          <Badge
            color="error"
            variant="dot"
            overlap="circular"
            invisible={true}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {icon}
          </Badge>
        ) : (
          icon
        );

        const isActive = route === currentPath || key === collapseName;
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={displayName}
              icon={wrappedIcon}
              active={isActive}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse name={displayName} icon={wrappedIcon} active={isActive} />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" flexDirection="column" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDTypography
            component="h6"
            variant="button"
            fontWeight="medium"
            color={textColor}
            mt={1}
          >
            {brandName}
          </MDTypography>
          <MDTypography variant="caption" color={textColor} mt={0.25}>
            Ver {APP_VERSION}
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
